/**
 * Privacy Policy for Jemmi
 */

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="mx-auto max-w-2xl bg-white rounded-xl shadow-sm p-8 lg:p-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
        <p className="text-slate-600 mb-8">Last updated: May 2026</p>

        <div className="prose prose-slate max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-bold text-slate-900">1. Introduction</h2>
            <p className="text-slate-700">
              Jemmi ("we", "us", "our") operates the Jemmi service. This page informs you of our
              policies regarding the collection, use, and disclosure of personal data when you use
              our Service and the choices you have associated with that data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">2. Information Collection and Use</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-900">Account Information</h3>
                <p className="text-slate-700 mt-2">
                  When you create an account, we collect:
                </p>
                <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                  <li>Email address or phone number</li>
                  <li>Display name</li>
                  <li>Hashed password (never stored in plain text)</li>
                  <li>Account creation date</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900">Content You Create</h3>
                <p className="text-slate-700 mt-2">
                  We store:
                </p>
                <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                  <li>Challenges you create (title, description, category, duration)</li>
                  <li>Submissions you post (photos, videos, captions)</li>
                  <li>Votes and interactions on other submissions</li>
                  <li>Comments and feedback</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900">Usage Data</h3>
                <p className="text-slate-700 mt-2">
                  We automatically collect:
                </p>
                <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                  <li>IP address</li>
                  <li>Device type and operating system</li>
                  <li>Browser type and version</li>
                  <li>Pages visited and time spent</li>
                  <li>Referrer URL</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">3. Content Moderation & Monitoring</h2>
            <p className="text-slate-700">
              To maintain community safety and compliance with our Content Guidelines, we use:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mt-3">
              <li>
                <strong>Automated Systems:</strong> Azure AI Content Safety to scan submissions for
                inappropriate content including sexual material, violence, hate speech, and other
                violations.
              </li>
              <li>
                <strong>User Reports:</strong> Community members can flag content they find
                inappropriate. Flagged submissions are temporarily hidden pending human review.
              </li>
              <li>
                <strong>Human Review:</strong> Our moderation team reviews flagged content and user
                reports to ensure compliance.
              </li>
            </ul>
            <p className="text-slate-700 mt-3">
              Moderation data may be used to improve our systems and detect patterns of abuse. We
              do not sell moderation data to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">4. Use of Information</h2>
            <p className="text-slate-700">We use your information to:</p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mt-3">
              <li>Provide, maintain, and improve our Service</li>
              <li>Send you technical notices and support messages</li>
              <li>Respond to your comments and questions</li>
              <li>Monitor and analyze usage patterns for security and improvement</li>
              <li>Prevent fraudulent transactions and other illegal activities</li>
              <li>Enforce our Terms of Service and other legal agreements</li>
              <li>Protect user safety and content quality through moderation</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">5. Security of Your Data</h2>
            <p className="text-slate-700">
              We implement appropriate technical and organizational security measures to protect
              your personal information against unauthorized access, alteration, disclosure, or
              destruction. This includes:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mt-3">
              <li>HTTPS encryption for all data in transit</li>
              <li>Password hashing using industry-standard algorithms</li>
              <li>Two-factor authentication support</li>
              <li>Regular security audits and penetration testing</li>
              <li>Secure data storage with access controls</li>
              <li>Employee training on data protection</li>
            </ul>
            <p className="text-slate-700 mt-3">
              However, no method of transmission over the Internet is 100% secure. While we strive
              to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">6. Data Retention</h2>
            <p className="text-slate-700">
              We retain your personal data for as long as necessary to provide our Service and
              fulfill the purposes outlined in this Privacy Policy. You can request deletion of your
              account and associated data at any time by contacting us at{' '}
              <a href="mailto:support@jemmi.fun" className="text-brand-600 hover:underline">
                support@jemmi.fun
              </a>
            </p>
            <p className="text-slate-700 mt-3">
              Note: Some data may be retained longer for legal compliance, fraud prevention, or
              safety purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">7. Third-Party Services</h2>
            <p className="text-slate-700">
              We use third-party services that may collect information used to identify you:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mt-3">
              <li>
                <strong>Azure Content Safety:</strong> For automated content moderation. Microsoft's
                privacy policy applies.
              </li>
              <li>
                <strong>Cloudflare:</strong> For hosting and CDN services. Data is processed in
                accordance with Cloudflare's privacy practices.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">8. Your Rights</h2>
            <p className="text-slate-700">
              Depending on your location, you may have the following rights:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mt-3">
              <li>
                <strong>Right of Access:</strong> You can request a copy of your personal data
              </li>
              <li>
                <strong>Right to Correction:</strong> You can request correction of inaccurate data
              </li>
              <li>
                <strong>Right to Deletion:</strong> You can request deletion of your data
              </li>
              <li>
                <strong>Right to Data Portability:</strong> You can request your data in a portable
                format
              </li>
              <li>
                <strong>Right to Opt-Out:</strong> You can opt out of certain data uses
              </li>
            </ul>
            <p className="text-slate-700 mt-3">
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:support@jemmi.fun" className="text-brand-600 hover:underline">
                support@jemmi.fun
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">9. Children's Privacy</h2>
            <p className="text-slate-700">
              Our Service is not directed to children under 13. We do not knowingly collect personal
              information from children under 13. If we become aware that a child under 13 has
              provided us with personal information, we will delete such data and terminate the
              child's account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">10. Changes to This Policy</h2>
            <p className="text-slate-700">
              We may update this Privacy Policy from time to time. We will notify you of any
              changes by posting the new Privacy Policy on this page and updating the "Last
              updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">11. Contact Us</h2>
            <p className="text-slate-700">
              If you have questions about this Privacy Policy or our privacy practices, please
              contact us at:
            </p>
            <div className="mt-3 text-slate-700">
              <p>Email: <a href="mailto:privacy@jemmi.fun" className="text-brand-600 hover:underline">privacy@jemmi.fun</a></p>
              <p>Support: <a href="mailto:support@jemmi.fun" className="text-brand-600 hover:underline">support@jemmi.fun</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
